import React, { memo, useState, Dispatch, FC, SetStateAction, ChangeEvent, useEffect, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, MenuItem, Grid } from "@mui/material";
import CrossBtnIcon from "assets/CrossBtnIcon";
import {
  ModalTitle,
  CustomDivider,
  ModalBody,
  CloseBtnContainer,
  CloseBtn,
  StyledSelect,
  CustomTextField,
  StyledModalFooter,
  SavedFiltersText,
  FilterNameContainer,
  FilterNameHeading,
  DeleteBtnWrapper,
  ModalBodyAllFilterContainer,
  DeleteButton,
  SaveButton,
  ApplyButton,
} from "./sideModal.styled";

import FiltersOptions from "./filtersOptions";
import { Filter } from './customSort.types';
import CustomButton from "../buttons/button/customButton";
import { CustomButtonContainer } from "../buttons/button/customButton.styled";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import { Close } from "@mui/icons-material";
import { FilterButton } from "modules/ac2/components/ac2Alerts/alerts.styled";


type SideModalProps = {
  isFilterModalOpen: boolean;
  setFilterModalOpen: Dispatch<SetStateAction<boolean>>;
  activeFilter: Filter | undefined;
  setActiveFilter: Dispatch<SetStateAction<Filter | undefined>>;
  filters: Filter[];
  getBaseFilter: any;
  setFilters: Dispatch<SetStateAction<Filter[]>>;
  underConstructionFilter: any;
  framework: any;
  setUnderConstructionFilter: Dispatch<SetStateAction<Filter>>;
  descriptions?: string[];
  selectedDescription?: string;
  selectedSectionTitle?: string;
  onFilterCreated?: () => void;
};

const SideModal = forwardRef<{
  handleSetActiveFilter: () => void;
}, SideModalProps>(
  ({
    isFilterModalOpen,
    setFilterModalOpen,
    activeFilter,
    setActiveFilter,
    filters,
    setFilters,
    getBaseFilter,
    underConstructionFilter,
    setUnderConstructionFilter,
    framework,
    descriptions,
    selectedDescription,
    selectedSectionTitle,
    onFilterCreated
  }, ref) => {
    const { t } = useTranslation();
    const [title, setTitleValue] = useState('');
    const [openCreateDialog, setOpenCreateDialog] = useState(false);

    

    const selectionChangeHandler = (event) => {
      const filter = filters.find((filter) => filter.id === event.target.value)
      if (filter) {
        setUnderConstructionFilter(filter);
        setTitleValue(filter.name);
      }
    };

    const handleResetFilter = () => {
      // Reset the current filter to a default one (local data change)
      const baseFilter = getBaseFilter(underConstructionFilter)
      setUnderConstructionFilter(baseFilter);

      // Pop off the current filter from the filter list
      const updatedFilters = filters.map((filter) => {
        if (underConstructionFilter.id === filter.id) {
          return baseFilter;
        }
        return filter;
      });

      // Set the filters avalable to select from the drop down
      setFilters(updatedFilters);
    }

    const handleDeleteFilter = () => {
      // Pop off the current filter from the filter list
      const newFilters = filters.filter((filter) => {
        if (underConstructionFilter.id !== filter.id) {
          return filter;
        }
      });
      // Set the filters avalable to select from the drop down
      setFilters(newFilters);

      // Reset the current filter to a default one (local data change)
      getBaseFilter()

      // Clear the filter from the table
      setActiveFilter(undefined);
    };

    function getFilterCount(updatedFilter) {
      let count = 0;

      for (let i = 0; i < updatedFilter.categories.length; i++) {
        for (let j = 0; j < updatedFilter.categories[i].children.length; j++) {
            if (updatedFilter.categories[i].children[j].isChecked) {
                count += 1;
            }
        }
      }  
      return count;
    }

    

    function handleSetActiveFilter() {
      // Add the textField as the name for the filter
      const activeFilter = {
        ...underConstructionFilter,
        name: title || underConstructionFilter.name
      }
      
      // Update the under construction array (local data change)
      setUnderConstructionFilter(activeFilter);

      // Update the active filter array (ui change)
      setActiveFilter(activeFilter);

      // Clear the local title state for a new one to be available
      setTitleValue('')
    }

    const handleCreateAndSaveFilter = () => {
      // Create a new filter object
      const newFilter = {
        ...underConstructionFilter,
        name: title || underConstructionFilter.name,
        id: Date.now().toString(), // or use uuid if you have it
      };

      // Add to filters array
      setFilters(prev => [...prev, newFilter]);

      // Set as active and under construction filter
      setActiveFilter(newFilter);
      setUnderConstructionFilter(newFilter);

      // Clear the input and close dialog
      setTitleValue('');
      setOpenCreateDialog(false);
      if (typeof onFilterCreated === "function") {
        onFilterCreated();
      }
    };
    

    const [openCategory, setOpenCategory] = useState<string | null>(null);
    const [hasAutoChecked, setHasAutoChecked] = useState(false);



    const updateFilter = (isChecked: boolean, itemName: string, categoryLabel: string) => {
      setUnderConstructionFilter(prev => {
        const updatedCategories = prev.categories.map(category => {
          if (category.label === categoryLabel) {
            const updatedChildren = category.children.map(child =>
              child.name === itemName ? { ...child, isChecked } : child
            );
            return { ...category, children: updatedChildren };
          }
          return category;
        });

        return { ...prev, categories: updatedCategories };
      });
    };

    // Expose handleSetActiveFilter to parent via ref
    useImperativeHandle(ref, () => ({
      handleSetActiveFilter
    }));

    return (
      <>
              <Drawer
        anchor="right"
        open={isFilterModalOpen}
        ModalProps={{ onClose: () => setFilterModalOpen(false) }}
      >
        
        <ModalBody>
          <ModalBodyAllFilterContainer>
            <ModalTitle>{t("INCIDENTS_PAGE.ALLFILTERS")}</ModalTitle>
            <CloseBtnContainer>
              <CloseBtn onClick={() => setFilterModalOpen(false)}>
                <CrossBtnIcon/>
              </CloseBtn>
            </CloseBtnContainer>
          </ModalBodyAllFilterContainer>

          <SavedFiltersText>{t("INCIDENTS_PAGE.SAVED_FILTERS")}</SavedFiltersText>

          <StyledSelect
            value={underConstructionFilter ? underConstructionFilter.id : "none"}
            defaultValue="none"
            onChange={selectionChangeHandler}
          >
            <MenuItem value="none" disabled sx={{ color: "#D3D3D3" }}>
              {t("INCIDENTS_PAGE.SELECT_SAVED_FILTERS")}
            </MenuItem>

            {filters.map((item) => {
              return (
                <MenuItem
                  key={item.id}
                  value={item.id}
                  data-testid={item.id}
                >
                  {item.name}
                </MenuItem>
              );
            })}
          </StyledSelect>

          <DeleteBtnWrapper>
            <DeleteButton
              onClick={handleDeleteFilter}
              data-testid="delete-button" 
              disabled={filters.length > 0 ? false : true}
            >
              {t("INCIDENTS_PAGE.DELETE")}
            </DeleteButton>
          </DeleteBtnWrapper>

          <CustomDivider />
          
          <FiltersOptions 
            framework={framework} 
            filter={underConstructionFilter} 
            updateFilter={updateFilter} 
            openCategory={openCategory}
            setOpenCategory={setOpenCategory}
            handleSetActiveFilter={handleSetActiveFilter} 
            hasAutoChecked={hasAutoChecked} 
            setHasAutoChecked={setHasAutoChecked}
            descriptions={descriptions}
            selectedDescription={selectedDescription ? [selectedDescription] : []}
            selectedSectionTitle={selectedSectionTitle}
           />

          

          <CustomDivider />

          <StyledModalFooter>
            <SaveButton
              className="save-button"
              data-testid="save-button"
              onClick={() => setOpenCreateDialog(true)}
              variant="outlined"
            >
              {t("INCIDENTS_PAGE.SAVEANDAPPLYFILTER")}
            </SaveButton>

            <ApplyButton
              className="save-button"
              data-testid="save-button"
              onClick={() => handleSetActiveFilter()}
              variant="outlined"
            >
              {t("INCIDENTS_PAGE.APPLY_FILTER")}
            </ApplyButton>

            <ApplyButton
              className="reset-button"
              onClick={handleResetFilter}
              data-testid="reset-button"
              variant="outlined"
            >
              {t("INCIDENTS_PAGE.RESET")}
            </ApplyButton>
          </StyledModalFooter>
        </ModalBody>
      </Drawer>

      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '1px',
            boxShadow: '0px 4px 24px rgba(0,0,0,0.12)',
            minWidth: 600,
            maxWidth: 500,
            minHeight: 296,
            backgroundColor: '#FFFFFF',
            px: 0,
            py: 0,
           
          }
        }}
      >
        <div className="dialog-header" style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F5F5F5' } }>
          <DialogTitle sx={{ p: 2, color: '#333333' }}>{t("INCIDENTS_PAGE.CREATE_FILTER")}</DialogTitle>
          <span style={{ cursor: 'pointer', color: 'black', marginRight: '16px' }} onClick={() => setOpenCreateDialog(false)}>
            <Close className="text-black" />
          </span>
        </div>
        <DialogContent>
          <FilterNameContainer>
            <label htmlFor="filter-name" style={{ color: '#333333' }}>{t("INCIDENTS_PAGE.FILTER_NAME")}</label>
            <CustomTextField
              id="filter-name"
              variant="outlined"
              fullWidth
              value={title}
              placeholder={underConstructionFilter.name}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setTitleValue(event.target.value);
              }}
            />
          </FilterNameContainer>


        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)} color="secondary" variant="outlined">
            {t("INCIDENTS_PAGE.CANCEL")}
          </Button>
          <Button onClick={() => handleCreateAndSaveFilter()} variant="contained">
            {t("INCIDENTS_PAGE.CREATE")}
          </Button>
        </DialogActions>
      </Dialog>
      </>
    );
  }
);

export default memo(SideModal);
